import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { PaymentForm } from '../components/PaymentForm';
import { isStripeEnabled } from '../lib/stripe';
import { CheckCircle, AlertCircle, CreditCard } from 'lucide-react';

interface Campaign {
  id: string;
  name: string;
  description: string | null;
  goal: number | null;
  totalRaised: number;
  percentComplete: number | null;
  donorCount: number;
}

interface Donation {
  id: string;
  amount: number;
  type: string;
  fund: string | null;
  campaign: string | null;
  createdAt: string;
}

interface PaymentIntent {
  clientSecret: string;
  paymentIntentId?: string;
  subscriptionId?: string;
  type: 'payment' | 'subscription';
}

interface StripeConfig {
  stripeConfigured: boolean;
}

const PRESET_AMOUNTS = [18, 36, 72, 180, 360];

const DONATION_TYPES = [
  { value: 'general', label: 'General Donation' },
  { value: 'tribute', label: 'Tribute Donation' },
  { value: 'yahrzeit', label: 'Yahrzeit Donation' },
];

type DonationStep = 'form' | 'payment' | 'success';

export function DonationsPage() {
  useAuth();
  const queryClient = useQueryClient();

  const [step, setStep] = useState<DonationStep>('form');
  const [showForm, setShowForm] = useState(false);
  const [amount, setAmount] = useState<number>(36);
  const [customAmount, setCustomAmount] = useState('');
  const [type, setType] = useState('general');
  const [tributeType, setTributeType] = useState<'in_honor' | 'in_memory'>('in_honor');
  const [tributeHonoree, setTributeHonoree] = useState('');
  const [selectedCampaign, setSelectedCampaign] = useState<string>('');
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurringInterval, setRecurringInterval] = useState<'monthly' | 'quarterly' | 'yearly'>('monthly');

  const [paymentIntent, setPaymentIntent] = useState<PaymentIntent | null>(null);

  // Check for payment success from redirect
  useEffect(() => {
    const hash = window.location.hash;
    if (hash.includes('payment_status=success')) {
      setStep('success');
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      queryClient.invalidateQueries({ queryKey: ['my-donations'] });
      // Clean up URL
      window.location.hash = 'donate';
    }
  }, [queryClient]);

  const { data: stripeConfig } = useQuery({
    queryKey: ['stripe-config'],
    queryFn: async () => {
      const response = await api.get<StripeConfig>('/payments/config');
      if (!response.success) throw new Error(response.error?.message);
      return response.data;
    },
  });

  const { data: campaigns } = useQuery({
    queryKey: ['campaigns'],
    queryFn: async () => {
      const response = await api.get<Campaign[]>('/donations/campaigns');
      if (!response.success) throw new Error(response.error?.message);
      return response.data || [];
    },
  });

  const { data: myDonations } = useQuery({
    queryKey: ['my-donations'],
    queryFn: async () => {
      const response = await api.get<Donation[]>('/donations/my');
      if (!response.success) throw new Error(response.error?.message);
      return response.data || [];
    },
  });

  // Create donation record first
  const createDonationMutation = useMutation({
    mutationFn: async () => {
      const donationAmount = customAmount ? parseFloat(customAmount) : amount;
      const response = await api.post<{ id: string }>('/donations', {
        amount: donationAmount,
        type,
        campaignId: selectedCampaign || undefined,
        tributeType: type === 'tribute' ? tributeType : undefined,
        tributeHonoree: type === 'tribute' ? tributeHonoree : undefined,
        isRecurring,
        recurringInterval: isRecurring ? recurringInterval : undefined,
      });
      if (!response.success) throw new Error(response.error?.message);
      return response.data!;
    },
  });

  // Create payment intent after donation is created
  const createPaymentIntentMutation = useMutation({
    mutationFn: async (donationId: string) => {
      const donationAmount = customAmount ? parseFloat(customAmount) : amount;
      const response = await api.post<PaymentIntent>('/payments/create-donation-intent', {
        donationId,
        amount: donationAmount,
        isRecurring,
        recurringInterval: isRecurring ? recurringInterval : undefined,
      });
      if (!response.success) throw new Error(response.error?.message);
      return response.data!;
    },
  });

  const handleProceedToPayment = async () => {
    try {
      // First create the donation record
      const donation = await createDonationMutation.mutateAsync();

      // Then create payment intent
      const intent = await createPaymentIntentMutation.mutateAsync(donation.id);
      setPaymentIntent(intent);
      setStep('payment');
    } catch (error) {
      console.error('Failed to create payment:', error);
    }
  };

  const handlePaymentSuccess = () => {
    setStep('success');
    queryClient.invalidateQueries({ queryKey: ['campaigns'] });
    queryClient.invalidateQueries({ queryKey: ['my-donations'] });
  };

  const handlePaymentCancel = () => {
    setStep('form');
    setPaymentIntent(null);
  };

  const handleDonateAnother = () => {
    setStep('form');
    setShowForm(true);
    setPaymentIntent(null);
    setCustomAmount('');
    setTributeHonoree('');
    setSelectedCampaign('');
    setIsRecurring(false);
  };

  const resetForm = () => {
    setShowForm(false);
    setStep('form');
    setPaymentIntent(null);
    setCustomAmount('');
    setTributeHonoree('');
    setSelectedCampaign('');
    setIsRecurring(false);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const effectiveAmount = customAmount ? parseFloat(customAmount) || 0 : amount;
  const isStripeReady = stripeConfig?.stripeConfigured && isStripeEnabled();
  const isLoading = createDonationMutation.isPending || createPaymentIntentMutation.isPending;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Support Our Community</h1>
          <p className="mt-2 text-gray-600">Your generosity helps sustain our programs and services</p>
        </div>

        {/* Success Message */}
        {step === 'success' && (
          <div className="bg-white rounded-xl shadow-sm border border-green-200 p-8 mb-8 text-center">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Thank You!</h2>
            <p className="text-gray-600 mb-6">
              Your donation has been processed successfully. A receipt will be sent to your email.
            </p>
            <button
              onClick={handleDonateAnother}
              className="px-6 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700"
            >
              Make Another Donation
            </button>
          </div>
        )}

        {/* Active Campaigns */}
        {step !== 'success' && campaigns && campaigns.length > 0 && (
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Active Campaigns</h2>
            <div className="grid gap-4 md:grid-cols-2">
              {campaigns.map((campaign) => (
                <div
                  key={campaign.id}
                  className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
                >
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{campaign.name}</h3>
                  {campaign.description && (
                    <p className="text-gray-600 text-sm mb-4">{campaign.description}</p>
                  )}
                  {campaign.goal && (
                    <div className="mb-4">
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-gray-600">{formatCurrency(campaign.totalRaised)} raised</span>
                        <span className="font-medium">{campaign.percentComplete}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-primary-600 h-2 rounded-full"
                          style={{ width: `${Math.min(campaign.percentComplete || 0, 100)}%` }}
                        />
                      </div>
                      <p className="text-sm text-gray-500 mt-1">
                        Goal: {formatCurrency(campaign.goal)}
                      </p>
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">{campaign.donorCount} donors</span>
                    <button
                      onClick={() => {
                        setSelectedCampaign(campaign.id);
                        setShowForm(true);
                        setStep('form');
                      }}
                      className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700"
                    >
                      Donate
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* General Donation Card */}
        {step !== 'success' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
            <div className="flex items-start justify-between mb-6">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Make a Donation</h2>
                <p className="text-gray-600 mt-1">Support our synagogue's mission</p>
              </div>
              {!showForm && step === 'form' && (
                <button
                  onClick={() => {
                    setSelectedCampaign('');
                    setShowForm(true);
                  }}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700"
                >
                  Donate Now
                </button>
              )}
            </div>

            {/* Stripe Not Configured Warning */}
            {showForm && !isStripeReady && (
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg mb-6 flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-yellow-800 font-medium">Online payments not available</p>
                  <p className="text-yellow-700 text-sm mt-1">
                    Payment processing is not configured. Please contact the office for other donation methods.
                  </p>
                </div>
              </div>
            )}

            {/* Payment Form Step */}
            {step === 'payment' && paymentIntent && (
              <div>
                <div className="flex items-center gap-3 mb-6 pb-4 border-b">
                  <div className="h-10 w-10 rounded-lg bg-primary-100 flex items-center justify-center">
                    <CreditCard className="h-5 w-5 text-primary-600" />
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">Complete Payment</h3>
                    <p className="text-sm text-gray-500">
                      {formatCurrency(effectiveAmount)}
                      {isRecurring && ` / ${recurringInterval}`}
                    </p>
                  </div>
                </div>
                <PaymentForm
                  clientSecret={paymentIntent.clientSecret}
                  amount={effectiveAmount}
                  type={paymentIntent.type}
                  onSuccess={handlePaymentSuccess}
                  onCancel={handlePaymentCancel}
                />
              </div>
            )}

            {/* Donation Form Step */}
            {showForm && step === 'form' && (
              <div className="space-y-6">
                {/* Amount Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Amount
                  </label>
                  <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mb-3">
                    {PRESET_AMOUNTS.map((preset) => (
                      <button
                        key={preset}
                        onClick={() => {
                          setAmount(preset);
                          setCustomAmount('');
                        }}
                        className={`py-2 px-3 rounded-lg text-sm font-medium border transition-colors ${
                          amount === preset && !customAmount
                            ? 'bg-primary-600 text-white border-primary-600'
                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        ${preset}
                      </button>
                    ))}
                    <input
                      type="number"
                      placeholder="Other"
                      value={customAmount}
                      onChange={(e) => setCustomAmount(e.target.value)}
                      className="py-2 px-3 rounded-lg text-sm border border-gray-300 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                  <p className="text-sm text-gray-500">
                    Amounts like 18, 36, 72 correspond to multiples of chai (life)
                  </p>
                </div>

                {/* Donation Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Donation Type
                  </label>
                  <select
                    value={type}
                    onChange={(e) => setType(e.target.value)}
                    className="w-full py-2 px-3 rounded-lg border border-gray-300 focus:ring-primary-500 focus:border-primary-500"
                  >
                    {DONATION_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>

                {/* Tribute Fields */}
                {type === 'tribute' && (
                  <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Tribute Type
                      </label>
                      <div className="flex gap-4">
                        <label className="flex items-center">
                          <input
                            type="radio"
                            value="in_honor"
                            checked={tributeType === 'in_honor'}
                            onChange={() => setTributeType('in_honor')}
                            className="mr-2"
                          />
                          In Honor Of
                        </label>
                        <label className="flex items-center">
                          <input
                            type="radio"
                            value="in_memory"
                            checked={tributeType === 'in_memory'}
                            onChange={() => setTributeType('in_memory')}
                            className="mr-2"
                          />
                          In Memory Of
                        </label>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Honoree Name
                      </label>
                      <input
                        type="text"
                        value={tributeHonoree}
                        onChange={(e) => setTributeHonoree(e.target.value)}
                        placeholder="Enter name"
                        className="w-full py-2 px-3 rounded-lg border border-gray-300 focus:ring-primary-500 focus:border-primary-500"
                      />
                    </div>
                  </div>
                )}

                {/* Recurring */}
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="recurring"
                    checked={isRecurring}
                    onChange={(e) => setIsRecurring(e.target.checked)}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <label htmlFor="recurring" className="text-sm text-gray-700">
                    Make this a recurring donation
                  </label>
                  {isRecurring && (
                    <select
                      value={recurringInterval}
                      onChange={(e) => setRecurringInterval(e.target.value as 'monthly' | 'quarterly' | 'yearly')}
                      className="ml-2 py-1 px-2 rounded border border-gray-300 text-sm"
                    >
                      <option value="monthly">Monthly</option>
                      <option value="quarterly">Quarterly</option>
                      <option value="yearly">Yearly</option>
                    </select>
                  )}
                </div>

                {/* Submit */}
                <div className="flex items-center justify-between pt-4 border-t">
                  <div className="text-lg font-semibold">
                    Total: {formatCurrency(effectiveAmount)}
                    {isRecurring && <span className="text-sm font-normal text-gray-500"> / {recurringInterval}</span>}
                  </div>
                  <div className="flex gap-3">
                    <button
                      onClick={resetForm}
                      className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleProceedToPayment}
                      disabled={isLoading || effectiveAmount <= 0 || !isStripeReady}
                      className="px-6 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 disabled:opacity-50"
                    >
                      {isLoading ? 'Processing...' : 'Continue to Payment'}
                    </button>
                  </div>
                </div>

                {(createDonationMutation.isError || createPaymentIntentMutation.isError) && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                    {createDonationMutation.error?.message || createPaymentIntentMutation.error?.message}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Donation History */}
        {myDonations && myDonations.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Your Donation History</h2>
            <div className="divide-y">
              {myDonations.map((donation) => (
                <div key={donation.id} className="py-3 flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900">{formatCurrency(donation.amount)}</p>
                    <p className="text-sm text-gray-500">
                      {donation.campaign || donation.fund || donation.type} &middot; {formatDate(donation.createdAt)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
